import { Badge } from '@heidi/design-system/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyH4,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Check } from 'lucide-react';
import { memo, type ReactNode } from 'react';

import type { PricingPlanCardFeature } from './pricing-plan-card-base';
import { PRICING_PLANS_MODAL_CARD_CLASSES } from './pricing-plan-card-base';
import { PricingPlanCardCtaButton } from './pricing-plan-card-cta-button';

export type TrialEndSeatLine = {
  label: ReactNode;
  description: string;
  badge?: ReactNode | null;
};

export type TrialEndDualSeatPlanCardProps = {
  audience: string;
  audienceColor: 'blue' | 'neutral';
  planName: string;
  /** Period tabs (Clinician) or locked yearly pill (Practice). */
  periodControl: ReactNode;
  standardSeat: TrialEndSeatLine;
  maxSeat: TrialEndSeatLine;
  ctaText: ReactNode;
  ctaIcon: ReactNode;
  ctaVariant: 'pro' | 'outline';
  ctaHelper: string;
  onCtaClick?: () => void;
  isCtaLoading?: boolean;
  isCtaDisabled?: boolean;
  ctaDisabledTooltip?: string;
  featuresTitle: ReactNode;
  features: PricingPlanCardFeature[];
  isHighlighted?: boolean;
  isPractice?: boolean;
  'data-testid'?: string;
};

function SeatLine({ label, description, badge }: TrialEndSeatLine) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <TypographyP2 className="text-text-primary">{label}</TypographyP2>
        {badge}
      </div>
      <TypographyCaption className="text-text-secondary">{description}</TypographyCaption>
    </div>
  );
}

function TrialEndDualSeatPlanCardComponent({
  audience,
  audienceColor,
  planName,
  periodControl,
  standardSeat,
  maxSeat,
  ctaText,
  ctaIcon,
  ctaVariant,
  ctaHelper,
  onCtaClick,
  isCtaLoading,
  isCtaDisabled,
  ctaDisabledTooltip,
  featuresTitle,
  features,
  isHighlighted,
  isPractice,
  'data-testid': testId,
}: TrialEndDualSeatPlanCardProps) {
  const cta =
    onCtaClick != null ? (
      <PricingPlanCardCtaButton
        variant={ctaVariant}
        icon={ctaIcon}
        onClick={onCtaClick}
        isCtaLoading={isCtaLoading}
        isCtaDisabled={isCtaDisabled}
        ctaText={ctaText}
      />
    ) : null;

  return (
    <div
      data-testid={testId}
      data-audience-color={audienceColor}
      data-cta-variant={ctaVariant}
      className={cn(
        'relative flex grow flex-col gap-6 rounded-xl border bg-surface p-6 newBrandDark:bg-surface-elevated',
        PRICING_PLANS_MODAL_CARD_CLASSES,
        isHighlighted
          ? isPractice
            ? 'border-tier-practice-border'
            : 'border-tier-pro-border'
          : 'border-border'
      )}
    >
      <div className="flex flex-col gap-4">
        <Badge size="sm" color={audienceColor} className="self-start">
          {audience}
        </Badge>
        <div className="flex items-center justify-between gap-4">
          <TypographyH4 className="text-text-primary">{planName}</TypographyH4>
          {periodControl}
        </div>
        <div className="flex flex-col gap-2">
          <SeatLine {...standardSeat} />
          <SeatLine {...maxSeat} />
        </div>
      </div>

      {cta != null ? (
        <div className="flex flex-col items-center gap-2">
          {isCtaDisabled && ctaDisabledTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block w-full">{cta}</span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{ctaDisabledTooltip}</TooltipContent>
            </Tooltip>
          ) : (
            cta
          )}
          <TypographyCaptionBold className="text-center text-text-secondary">
            {ctaHelper}
          </TypographyCaptionBold>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <TypographyCaption className="font-medium text-text-primary">
          {featuresTitle}
        </TypographyCaption>
        <ul className="flex flex-col gap-2" role="list">
          {features.map(({ description, icon: Icon }, index) => (
            <li key={description ?? index} className="flex items-start gap-2">
              {Icon ? (
                <Icon className="size-4 shrink-0 text-text-secondary" aria-hidden />
              ) : (
                <Check className="size-4 shrink-0 text-text-secondary" aria-hidden />
              )}
              <TypographyCaption className="min-w-0 text-text-primary">
                {description}
              </TypographyCaption>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export const TrialEndDualSeatPlanCard = memo(TrialEndDualSeatPlanCardComponent);
